import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactI18n from 'react-i18n';
import {
  Box,
  Container,
  PageStateMessage,
  Spacer,
  TextBody,
  TextCaption,
  TextH2
} from '@directsoftware/ui-kit-web-admin';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWlsReceipt } from '../../utils/wls-api-client';
import isWlsBookingNotFoundError from '../../utils/wls-booking-error';
import {
  selectReceiptState,
  updateReceiptSlice
} from '../../redux/slices/receipt';
import { REQUIREMENT } from '../../utils/guest-stay-state';
import useGuestStay from '../my-stay/use-guest-stay';
import FooterFinal from '../layout/footer-final';
import DeliveryLocationMap from './shared/delivery-location-map';
import { useDetectMobile } from '../../../shared/hooks/useDetectMobile';
import { getBrandThemeColors } from '../../../shared/brandThemeColors';
import {
  receiptTheme,
  receiptAddress,
  showMobilePay,
  isChannelCollected,
  showChecklistCard
} from '../../utils/receipt-redesign';
import ReceiptHeader from './shared/redesign-header';
import { ChecklistCard, DetailsCard } from './shared/redesign-cards';
import PaymentSummary from './shared/redesign-payment';
import BalanceDueCta from './shared/balance-due-cta';

const ReceiptV2 = props => {
  const { isMobile } = useDetectMobile();
  const dispatch = useDispatch();
  const receipt = useSelector(selectReceiptState);
  const brand = useSelector(state => state.brand);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const { stayState, loading: stayLoading, readiness } = useGuestStay(
    props.match.params.booking_code,
    props.match.params.slug
  );
  const paymentState = stayState && stayState.requirements[REQUIREMENT.payment];
  const mobilePay =
    showMobilePay(
      receipt,
      paymentState,
      stayState && stayState.lifecycle,
      stayLoading
    ) && !isChannelCollected(readiness, paymentState);
  const instructions =
    stayState && stayState.requirements[REQUIREMENT.checkInInstructions];
  const hasChecklist = showChecklistCard(stayState);
  const location = receipt.location || {};
  const latitude = Number(location.geo_latitude);
  const longitude = Number(location.geo_longitude);
  const hasMapLocation =
    location.geo_latitude != null &&
    location.geo_longitude != null &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    (latitude !== 0 || longitude !== 0);

  useEffect(() => {
    fetchWlsReceipt(brand, props.match.params.booking_code)
      .then(response => {
        if (!response.data || !response.data.booking) {
          setNotFound(true);
        } else {
          dispatch(
            updateReceiptSlice({
              ...response.data,
              refund_eligibility: response.data.refund_eligibility || null
            })
          );
        }
        setLoading(false);
      })
      .catch(error => {
        console.error(error);
        if (isWlsBookingNotFoundError(error)) setNotFound(true);
        else setLoadError(true);
        setLoading(false);
      });
  }, []);

  return (
    <>
      <div
        className={`receiptRedesignPage${
          mobilePay ? ' receiptRedesignPage--hasMobilePay' : ''
        }`}
      >
        <Container variation={isMobile ? 'full-width' : 'extended-wls'}>
          {loading && (
            <PageStateMessage
              iconProps={{ name: 'spinner', spin: true }}
              headline="Getting your booking details..."
              description=""
            />
          )}
          {!loading && notFound && (
            <PageStateMessage
              iconProps={{ name: 'emptySet' }}
              headline="Booking Not Found"
              description="We couldn't find a booking with that confirmation code. Please check the link and try again."
            />
          )}
          {!loading && loadError && (
            <PageStateMessage
              iconProps={{ name: 'exclamationTriangle' }}
              headline="Unable to Load Booking"
              description="We couldn't load your booking details. Please try again later."
            />
          )}
          {!loading && !notFound && !loadError && (
            <main
              className={`receiptRedesign${
                mobilePay ? ' receiptRedesign--hasMobilePay' : ''
              }`}
              style={receiptTheme(
                getBrandThemeColors(brand.brand_info).color_secondary,
                getBrandThemeColors(brand.brand_info).color_primary
              )}
            >
              <ReceiptHeader
                receipt={receipt}
                stayState={stayState}
                brand={brand}
              />
              <Box
                className={`receiptRedesign__columns${
                  hasChecklist ? '' : ' receiptRedesign__columns--noChecklist'
                }`}
              >
                {hasChecklist && (
                  <Box className="receiptRedesign__checklistArea">
                    <ChecklistCard
                      receipt={receipt}
                      stayState={stayState}
                      currency={brand.currency}
                      brand={brand}
                    />
                  </Box>
                )}
                <Box className="receiptRedesign__right">
                  <PaymentSummary
                    receipt={receipt}
                    brand={brand}
                    paymentState={
                      stayState && stayState.requirements[REQUIREMENT.payment]
                    }
                    depositState={
                      stayState &&
                      stayState.requirements[REQUIREMENT.securityDeposit]
                    }
                    stayLoading={stayLoading}
                    lifecycle={stayState && stayState.lifecycle}
                    channelPaid={isChannelCollected(readiness, paymentState)}
                  />
                </Box>
                <Box className="receiptRedesign__detailsArea">
                  <DetailsCard receipt={receipt} />
                </Box>
                <Box className="receiptRedesign__mapArea">
                  {receipt.location && (
                    <section
                      className="receiptRedesign__location"
                      aria-labelledby="receipt-location-heading"
                    >
                      <TextH2
                        renderAs="h2"
                        isFullWidth
                        id="receipt-location-heading"
                      >
                        {ReactI18n.getIntlMessage(
                          'cx.receipt.redesign.where_staying'
                        )}
                      </TextH2>
                      {receiptAddress(receipt.location) && (
                        <TextBody
                          renderAs="p"
                          isFullWidth
                          weight="semibold"
                          textColor="dark-gray"
                        >
                          {receiptAddress(receipt.location)}
                        </TextBody>
                      )}
                      {(!receipt.location.adr_street || hasMapLocation) && (
                        <div
                          className={`receiptRedesign__locationNote${
                            receipt.location.adr_street
                              ? ' receiptRedesign__locationNote--map'
                              : ''
                          }`}
                        >
                          <TextCaption weight="regular" isFullWidth>
                            {ReactI18n.getIntlMessage(
                              receipt.location.adr_street
                                ? 'cx.receipt.redesign.exact_location'
                                : `cx.receipt.redesign.${
                                    instructions &&
                                    instructions.status === 'complete'
                                      ? 'address_sent'
                                      : 'address_coming'
                                  }`
                            )}
                          </TextCaption>
                        </div>
                      )}
                      <DeliveryLocationMap
                        locationState={receipt.location}
                        mapOnly
                      />
                    </section>
                  )}
                </Box>
              </Box>
              {mobilePay && (
                <div className="receiptRedesign__mobilePay">
                  <BalanceDueCta
                    paymentState={paymentState}
                    isResolving={stayLoading}
                    isFullWidth
                  />
                </div>
              )}
            </main>
          )}
        </Container>
        <div className="receiptRedesignPage__footerSpacer">
          <Spacer size="l" />
        </div>
        <div className="receiptRedesignPage__footer">
          <FooterFinal />
        </div>
      </div>
    </>
  );
};

ReceiptV2.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      booking_code: PropTypes.string,
      slug: PropTypes.string
    }).isRequired
  }).isRequired
};

export default ReceiptV2;
